import React, { useCallback, forwardRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import PropTypes from 'prop-types'

import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import applicationSchema from '@galo-monitor/forms/schemas/root/application.schema'
import { useT } from '@galo-monitor/i18n'
import { useSnackbar } from '@galo-monitor/snackbar'
import { ApplicationActions } from '@galo-monitor/stores/application'
import { getContextIdBySlug, selectContexts } from '@galo-monitor/stores/context/selectorContext'
import Form from '@galo-monitor/web-components/Form'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import InputText from '@galo-monitor/web-components/InputText'

import { Container } from './styles'

const ApplicationForm = forwardRef((props, formRef) => {
  const { application, loading, setLoading, onSuccess, onError } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()
  const contexts = useSelector(selectContexts)

  const contextOptions = useMemo(
    () => map(contexts, item => ({ value: item.contextId.toString(), label: item.name })),
    [contexts]
  )

  const handleSubmit = useCallback(
    ({ context, ...data }) => {
      setLoading(true)
      dispatch(ApplicationActions.saveApplication(
        {
          ...application,
          ...data
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('permission', { howMany: 1 }),
            action: toLower(t(isEmpty(application) ? 'created' : 'updated', { gender: 'female', howMany: 1 }))
          }))
          onSuccess()
        },
        onError
      ))
    },
    [application, dispatch, onError, onSuccess, setLoading, snackbar, t]
  )

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ applicationSchema }
      onSubmit={ handleSubmit }
    >
      <Container>
        <InputText
          name="name"
          label={ t('name') }
          defaultValue={ application.name }
          disabled={ loading }
          fullWidth
        />

        <InputSelect
          name="context_id"
          label={ t('context') }
          defaultValue={ (getContextIdBySlug(application.contextSlug) || '').toString() }
          disabled={ loading }
          options={ contextOptions }
          fullWidth
        />
      </Container>
    </Form>
  )
})

ApplicationForm.propTypes = {
  application: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

ApplicationForm.defaultProps = {
  application: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default ApplicationForm
